import {ProvidersMedicalGuide} from 'domain/usecases/private/guide-flow/medical-guide/get-providers-v3';
import {useHealthProvidersV3Query} from 'presentation/modules/guide/health/queries/useHealthProvidersV3Query';
import {useMemo, useState} from 'react';

import {useAccountInformation, useQueryParams} from 'presentation/pages';
import {FeaturesEnum, HealthGuideServicesTypeEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useReverseNetworkOrder} from 'presentation/hooks/useReverseNetworkOrder';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {GroupedProvidersByNetworkProps} from 'presentation/modules/guide/health/ui/pages/HealthGuide/types';
import {
	isTelemedicine,
	isAccreditedNetwork,
	isSelfNetwork,
} from 'presentation/modules/guide/health/ui/pages/HealthGuide/utils/providerFilters';

export function useHealthProviderResults() {
	const {SEARCH_PARAMS} = useQueryParams();
	const [openDetails, setOpenDetails] = useState(false);
	const [provider, setProvider] = useState<ProvidersMedicalGuide>();
	const {isEarlyAccess} = useAccountInformation();
	const {isContractSuspended} = useContractStatus();
	const {shouldReverseNetworkOrder} = useReverseNetworkOrder({flowType: 'GUIDE'});

	const {serviceType, comingFrom} = SEARCH_PARAMS;

	const {data, isFetchingNextPage, isLoading, isError, refetch, loadMore} =
		useHealthProvidersV3Query();

	const {isEnabled: showTradeNameFlag} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NMFANTASIA_GUIA_MEDICO,
	);

	const onlyTeleInOthersServicesTypes =
		data?.length === 1 &&
		isTelemedicine(data[0]) &&
		serviceType !== HealthGuideServicesTypeEnum.OUTPATIENT;
	const isEmpty = !data || data.length === 0 || onlyTeleInOthersServicesTypes;

	const groupedProviders = useMemo(() => {
		const grouped: GroupedProvidersByNetworkProps = {
			telemedicina: [],
			redePropria: [],
			redeCredenciada: [],
		};

		if (!data) return grouped;

		data.forEach(provider => {
			const isTele = isTelemedicine(provider);
			const isSelf = isSelfNetwork(provider);
			const isAccredited = isAccreditedNetwork(provider);

			if (isTele) {
				grouped.telemedicina.push(provider);
				return;
			}

			if (isSelf) {
				grouped.redePropria.push(provider);
				return;
			}

			if (isAccredited) {
				grouped.redeCredenciada.push(provider);
			}
		});

		return grouped;
	}, [data]);

	const {redePropria = [], telemedicina = [], redeCredenciada = []} = groupedProviders;

	const showTelehealth =
		telemedicina.length > 0 &&
		!isContractSuspended &&
		serviceType === HealthGuideServicesTypeEnum.OUTPATIENT &&
		!isEarlyAccess;

	const showSelfNetwork = redePropria.length > 0;
	const showAccreditedNetwork = redeCredenciada.length > 0;
	const showDivider = redePropria.length > 0 && redeCredenciada.length > 0;

	const showNetworkAlert =
		!isEarlyAccess &&
		comingFrom !== FlowEnum.EXAM &&
		serviceType !== HealthGuideServicesTypeEnum.AUXILIARY_SERVICES;

	return {
		isLoading,
		isError,
		refetch,
		isEmpty,
		showTelehealth,
		shouldReverseNetworkOrder,
		redeCredenciada,
		redePropria,
		showTradeNameFlag,
		loadMore,
		isFetchingNextPage,
		openDetails,
		setOpenDetails,
		provider,
		setProvider,
		showSelfNetwork,
		showAccreditedNetwork,
		showDivider,
		showNetworkAlert,
	};
}
